import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PATH_TRANSLATIONS, resolveSupportedLang } from '../../shared/config/localized-paths';
import { NavItem } from '../models/nav-item.model';

/**
 * Provides shared navigation state (mobile drawer open/close) and the
 * primary nav item data consumed by both the desktop and mobile nav components.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly mobileNavOpenSubject = new BehaviorSubject<boolean>(false);
  readonly isMobileNavOpen$ = this.mobileNavOpenSubject.asObservable();

  constructor(private readonly _translateService: TranslateService) { }

  closeMobileNav(): void {
    this.mobileNavOpenSubject.next(false);
  }

  toggleMobileNav(): void {
    this.mobileNavOpenSubject.next(!this.mobileNavOpenSubject.value);
  }

  /**
   * Primary navigation items displayed in the desktop nav row (Row 2)
   * and inside the mobile drawer.
   */
  readonly primaryNavItems: NavItem[] = [
    {
      labelEn: 'Home',
      labelEs: 'Inicio',
      routeKey: 'home',
    },
    {
      labelEn: 'My Classroom Economy',
      labelEs: 'Mi Economía en el Aula',
      hasDropdown: true,
      dropdownItems: [
        { labelEn: 'Digital', labelEs: 'Digital', routeKey: 'digital' },
        { labelEn: 'Paper', labelEs: 'Papel', routeKey: 'overview' },
      ],
      activeForRouteKeys: ['gradesk-1', 'grades2-3', 'grades4-5', 'grade6', 'grades7-8', 'grades9-10', 'grades11-12'],
    },
    {
      labelEn: 'My Classroom Investor',
      labelEs: 'Mi Aula Inversionista',
      hasDropdown: true,
      dropdownItems: [
        { labelEn: 'Overview', labelEs: 'Descripción general', routeKey: 'invest' },
        { labelEn: 'Learning Library', labelEs: 'Biblioteca de Aprendizaje', routeKey: 'learning-library' },
        { labelEn: 'National Investment Challenge', labelEs: 'Desafio Nacional de Inversion', routeKey: 'nic' },
      ],
      activeForRouteKeys: ['invest', 'learning-library', 'nic'],
    },
    {
      labelEn: 'About us',
      labelEs: 'Sobre nosotros',
      hasDropdown: true,
      dropdownItems: [
        { labelEn: 'History', labelEs: 'Historia', routeKey: 'aboutus' },
        { labelEn: 'Webinar sign-up', labelEs: 'Registro para el seminario', routeKey: 'webinar' },
        { labelEn: 'Program updates', labelEs: 'Actualizaciones del programa', routeKey: 'updates' },
        { labelEn: 'Testimonials', labelEs: 'Testimonios', routeKey: 'testimonials' },
      ],
    },
  ];

  get loginItems(): NavItem[] {
    const resolvedLang = resolveSupportedLang(this._translateService.currentLang);
    const loginPath = PATH_TRANSLATIONS[resolvedLang]['login'];

    return [
      {
        id: 'mce-digital-login',
        labelEn: 'My Classroom Economy',
        labelEs: 'Mi Economía en el Aula',
        href: `${environment.digitalUiBaseUrl}/${resolvedLang}/${loginPath}`,
        isExternal: true,
      },
      {
        id: 'mci-login',
        labelEn: 'My Classroom Investor',
        labelEs: 'Mi Aula Inversionista',
        href: `${environment.investmentUiBaseUrl}/login`,
        isExternal: true,
      },
    ];
  }
}
